import { Fragment } from 'react';
import { useShallow } from 'zustand/shallow';
import { useGcsStore } from '../../../store/useGcsStore';
import { useMissionVisibilityStore } from '../../../store/useMissionVisibilityStore';
import { useSelectedVehicleStore } from '../../../store/useSelectedVehicleStore';
import { SingleMissionPointMarkers } from './SingleMissionPointMarkers';
import { SingleMissionLaunchpadMarker } from './SingleMissionLaunchpadMarker';
import { SingleMissionRouteLockedSegmentsPolyline } from './SingleMissionRouteLockedSegmentsPolyline';
import { SingleMissionRoutePolyline } from './SingleMissionRoutePolyline';
import { SingleMissionVehicleMarker } from './SingleMissionVehicleMarker';
import { SingleMissionFailureModeRouteMapElements } from './SingleMissionFailureModeRouteMapElements';

interface GcsMissionsProps {
  gcsId: string;
}

export const GcsMissions = ({ gcsId }: GcsMissionsProps) => {
  const missionVisibility = useMissionVisibilityStore((state) => state.missionVisibility);
  const selectedVehicle = useSelectedVehicleStore((state) => state.selectedVehicle);

  const filteredMissionIds = useGcsStore(
    useShallow((state) => {
      const gcsData = state.data.get(gcsId);
      if (!gcsData?.liveMissionsMap) return [];

      const allMissions = Array.from(gcsData.liveMissionsMap.values());

      // Filter missions based on visibility setting
      let filteredMissions = allMissions;
      if (missionVisibility === 'selected vehicle') {
        if (selectedVehicle && selectedVehicle.gcsId === gcsId) {
          filteredMissions = allMissions.filter((mission) => mission.vehicleId === selectedVehicle.vehicleId);
        } else {
          // No vehicle selected or wrong GCS, show no missions
          filteredMissions = [];
        }
      }

      return filteredMissions.map((mission) => mission.missionId);
    })
  );

  const allMissionIds = useGcsStore(
    useShallow((state) => {
      const gcsData = state.data.get(gcsId);
      if (!gcsData?.liveMissionsMap) return [];

      const allMissions = Array.from(gcsData.liveMissionsMap.values());
      return allMissions.map((mission) => mission.missionId);
    })
  );

  return (
    <>
      {/* Mission markers that respect visibility filter */}
      {filteredMissionIds.map((missionId) => (
        <Fragment key={`mission-elements-${gcsId}-${missionId}`}>
          <SingleMissionLaunchpadMarker gcsId={gcsId} missionId={missionId} />
          <SingleMissionPointMarkers gcsId={gcsId} missionId={missionId} />
        </Fragment>
      ))}

      {/* Draw all mission routes first */}
      {filteredMissionIds.map((missionId) => (
        <Fragment key={`mission-route-${gcsId}-${missionId}`}>
          <SingleMissionRoutePolyline gcsId={gcsId} missionId={missionId} />
        </Fragment>
      ))}

      {/* Draw locked segments after routes so they always appear above route lines */}
      {filteredMissionIds.map((missionId) => (
        <Fragment key={`mission-locked-segments-${gcsId}-${missionId}`}>
          <SingleMissionRouteLockedSegmentsPolyline gcsId={gcsId} missionId={missionId} />
        </Fragment>
      ))}

      {/* Draw failure mode route elements after route/locked segment lines */}
      {filteredMissionIds.map((missionId) => (
        <Fragment key={`mission-failure-mode-route-${gcsId}-${missionId}`}>
          <SingleMissionFailureModeRouteMapElements gcsId={gcsId} missionId={missionId} />
        </Fragment>
      ))}

      {/* Vehicle markers always show for all missions */}
      {allMissionIds.map((missionId) => (
        <SingleMissionVehicleMarker key={`mission-vehicle-${gcsId}-${missionId}`} gcsId={gcsId} missionId={missionId} />
      ))}
    </>
  );
};
